import { shortId, useAuthStore } from '@bemor/web-kit';

import type { StaffMember } from '@/api/types';
import { P } from '@/lib/permissions';
import { useList } from '@/lib/queries';

/** The signed-in staff member's user id (the `usr_` id the services stamp as actor, assignee, requester). */
export function useMe(): string {
    return useAuthStore((s) => s.session?.actor.id ?? '');
}

/** Platform staff: the platform-only screens (and, BM-68, the platform host they enter tenant boards from). */
export function useIsPlatform(): boolean {
    return useAuthStore((s) => s.session?.tenantScope === 'platform');
}

/** An actor id for display: "you" for the session's own id, else the short form. */
export function who(id: string | null | undefined, me: string): string {
    if (!id) return '—';
    if (id === me) return 'you';
    if (id === 'system') return 'system';
    return shortId(id);
}

/**
 * The staff of this tenant by staff id (`stf_`) and by user id (`usr_`), for showing names where the services
 * send ids (approvals, the requester and the subject of a staff authenticator reset). Only staff managers can
 * read the list; for everyone else the lookups are empty and the screens fall back to the ids.
 */
export function useStaffDirectory(): {
    byStaffId: (id: string | null | undefined) => StaffMember | undefined;
    byUserId: (id: string | null | undefined) => StaffMember | undefined;
} {
    const can = useAuthStore((s) => s.can);
    const list = useList<StaffMember>(
        '/staff/staff',
        { limit: 200 },
        can(P.staffManage),
    );
    const items = list.data?.data.items ?? [];
    return {
        byStaffId: (id) => (id ? items.find((m) => m.id === id) : undefined),
        byUserId: (id) =>
            id ? items.find((m) => m.user.id === id) : undefined,
    };
}

/** An actor for display: "you", a staff member's name when known, "system", else the short id. */
export function whoName(
    id: string | null | undefined,
    me: string,
    directory: ReturnType<typeof useStaffDirectory>,
): string {
    if (id && id !== me) {
        const m = directory.byUserId(id);
        if (m) return m.user.name;
    }
    return who(id, me);
}
